import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, ImagePlus, Trash2 } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { getProductById, uploadProductImage, upsertProductAdmin } from '../services/productService';
import { getCategories, upsertCategory } from '../services/categoryService';

const tagsArrayToObj = (tags) => {
  const arr = Array.isArray(tags) ? tags : [];
  return {
    newArrival: arr.includes('newArrival') || arr.includes('new_arrival') || arr.includes('new'),
    featured: arr.includes('featured'),
  };
};

const AdminProductEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const fileInputRef = useRef(null);
  const colorFileInputRef = useRef(null);

  const currencyCodes = ['USD', 'EUR', 'GBP', 'INR', 'CAD', 'AUD', 'JPY'];

  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [originalProduct, setOriginalProduct] = useState(null);

  const [categories, setCategories] = useState(() => {
    try {
      return getCategories({ includeHidden: true });
    } catch {
      return [];
    }
  });
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategorySlug, setNewCategorySlug] = useState('');

  const [form, setForm] = useState({
    name: '',
    description: '',
    categories: ['new-arrivals'],
    tags: {
      newArrival: true,
      featured: false,
    },
    prices: {
      USD: '',
      EUR: '',
      GBP: '',
      INR: '',
      CAD: '',
      AUD: '',
      JPY: '',
    },
    compareAtPrices: {
      USD: '',
      EUR: '',
      GBP: '',
      INR: '',
      CAD: '',
      AUD: '',
      JPY: '',
    },
    stockQuantity: 0,
    variantStock: {},
    status: 'active',
    visible: true,
    featured: false,
    sizes: ['S', 'M', 'L'],
    colors: ['BLACK'],
  });

  const ensureVariantStock = (nextForm) => {
    const sizes = Array.isArray(nextForm?.sizes) ? nextForm.sizes.map((s) => String(s).toUpperCase()) : [];
    const colors = Array.isArray(nextForm?.colors) ? nextForm.colors.map((c) => String(c).toUpperCase()) : [];
    const raw = nextForm?.variantStock && typeof nextForm.variantStock === 'object' ? nextForm.variantStock : {};
    const out = {};
    for (const c of colors) {
      for (const s of sizes) {
        const key = `${c}|${s}`;
        const n = Number(raw[key]);
        out[key] = Number.isFinite(n) ? n : 0;
      }
    }
    return out;
  };

  const getTotalVariantStock = (variantStock) => {
    const raw = variantStock && typeof variantStock === 'object' ? variantStock : {};
    return Object.values(raw).reduce((sum, v) => sum + (Number(v) || 0), 0);
  };

  const [images, setImages] = useState([]);
  const [primaryImageIndex, setPrimaryImageIndex] = useState(0);
  const [colorImages, setColorImages] = useState({});
  const [activeColorUpload, setActiveColorUpload] = useState('');
  const [touched, setTouched] = useState({});
  const [saving, setSaving] = useState(false);

  const [sizeInput, setSizeInput] = useState('');
  const [colorInput, setColorInput] = useState('');

  const normalizeToken = (value) => String(value || '').trim().toUpperCase();

  const addToken = (key, raw) => {
    const token = normalizeToken(raw);
    if (!token) return;
    setForm((prev) => {
      const current = Array.isArray(prev[key]) ? prev[key] : [];
      if (current.includes(token)) return prev;
      return { ...prev, [key]: [...current, token] };
    });
  };

  const removeToken = (key, token) => {
    setForm((prev) => {
      const current = Array.isArray(prev[key]) ? prev[key] : [];
      return { ...prev, [key]: current.filter((x) => x !== token) };
    });
  };

  useEffect(() => {
    const refreshCategories = () => {
      try {
        setCategories(getCategories({ includeHidden: true }));
      } catch {
        setCategories([]);
      }
    };
    refreshCategories();
    window.addEventListener('ditris:categories', refreshCategories);
    return () => window.removeEventListener('ditris:categories', refreshCategories);
  }, []);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      setNotFound(false);

      if (!id) {
        setNotFound(true);
        setIsLoading(false);
        return;
      }

      try {
        const product = await getProductById(id);
        if (!product) {
          setNotFound(true);
          setIsLoading(false);
          return;
        }

        setOriginalProduct(product);

        const productCategories = Array.isArray(product.categories)
          ? product.categories
          : typeof product.category === 'string' && product.category
            ? [product.category]
            : ['new-arrivals'];

        setCategories((prev) => {
          const next = [...prev];
          productCategories.forEach((slug) => {
            if (!slug) return;
            if (!next.find((c) => c.slug === slug)) {
              next.push({ id: slug, name: String(slug).toUpperCase(), slug, visible: true, order: next.length });
            }
          });
          return next;
        });

        setForm({
          name: String(product.name || ''),
          description: String(product.description || ''),
          categories: productCategories,
          tags: tagsArrayToObj(product.tags),
          prices: (() => {
            const base = {
              USD: '',
              EUR: '',
              GBP: '',
              INR: '',
              CAD: '',
              AUD: '',
              JPY: '',
            };
            const incoming = product.prices && typeof product.prices === 'object' ? product.prices : null;
            if (incoming) {
              for (const c of Object.keys(base)) {
                if (Object.prototype.hasOwnProperty.call(incoming, c)) base[c] = incoming[c];
              }
              return base;
            }
            if (product.price !== null && product.price !== undefined && product.price !== '') {
              base.USD = product.price;
            }
            return base;
          })(),
          compareAtPrices: (() => {
            const base = {
              USD: '',
              EUR: '',
              GBP: '',
              INR: '',
              CAD: '',
              AUD: '',
              JPY: '',
            };
            const incoming = product.compareAtPrices && typeof product.compareAtPrices === 'object' ? product.compareAtPrices : null;
            if (incoming) {
              for (const c of Object.keys(base)) {
                if (Object.prototype.hasOwnProperty.call(incoming, c)) base[c] = incoming[c];
              }
              return base;
            }
            if (product.compareAtPrice !== null && product.compareAtPrice !== undefined && product.compareAtPrice !== '') {
              base.USD = product.compareAtPrice;
            }
            return base;
          })(),
          stockQuantity: Number(product.stockQuantity ?? 0),
          variantStock: product?.variantStock && typeof product.variantStock === 'object' ? product.variantStock : {},
          status: product.status || 'active',
          visible: product.visible !== false,
          featured: Boolean(product.featured),
          sizes: Array.isArray(product.sizes) && product.sizes.length > 0 ? product.sizes : ['S', 'M', 'L'],
          colors: Array.isArray(product.colors) && product.colors.length > 0 ? product.colors : ['BLACK'],
        });

        setForm((prev) => {
          const variantStock = ensureVariantStock(prev);
          const stockQuantity = getTotalVariantStock(variantStock);
          return { ...prev, variantStock, stockQuantity };
        });

        const existingImages = Array.isArray(product.images) ? product.images : [];
        setImages(
          existingImages.map((url) => ({
            id: `existing-${String(url).slice(0, 24)}-${Math.random().toString(16).slice(2)}`,
            file: null,
            previewUrl: url,
          }))
        );
        setPrimaryImageIndex(Number(product.primaryImageIndex ?? 0));

        const incomingColorImages = product?.colorImages && typeof product.colorImages === 'object' ? product.colorImages : {};
        setColorImages(() => {
          const colors = Array.isArray(product.colors) ? product.colors.map((c) => String(c).toUpperCase()) : [];
          const out = {};
          colors.forEach((c) => {
            const entry = incomingColorImages[c] && typeof incomingColorImages[c] === 'object' ? incomingColorImages[c] : null;
            const imgs = entry && Array.isArray(entry.images) ? entry.images : [];
            const idx = Number(entry?.primaryImageIndex ?? 0);
            out[c] = {
              images: imgs.map((url) => ({
                id: `existing-${c}-${String(url).slice(0, 24)}-${Math.random().toString(16).slice(2)}`,
                file: null,
                previewUrl: url,
              })),
              primaryImageIndex: Number.isFinite(idx) && idx >= 0 ? idx : 0,
            };
          });
          return out;
        });
      } catch (e) {
        toast({
          title: 'Failed to load product',
          description: String(e?.message || 'Please try again.'),
        });
        setNotFound(true);
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [id]);

  const errors = useMemo(() => {
    const next = {};

    if (!form.name.trim()) next.name = 'Product name is required.';
    if (!form.description.trim()) next.description = 'Product description is required.';
    if (!form.categories || form.categories.length === 0) next.categories = 'Select at least one category.';

    const rawPrices = form.prices && typeof form.prices === 'object' ? form.prices : {};
    const definedPrices = [];
    const usdCandidate = rawPrices.USD;
    const usdNumber = usdCandidate === '' || usdCandidate === null || usdCandidate === undefined ? null : Number(usdCandidate);
    for (const code of currencyCodes) {
      const raw = rawPrices[code];
      if (raw === '' || raw === null || raw === undefined) continue;
      const n = Number(raw);
      if (Number.isNaN(n)) {
        next[`prices_${code}`] = `${code} price must be a number.`;
        continue;
      }
      if (n < 0) {
        next[`prices_${code}`] = `${code} price must be 0 or greater.`;
        continue;
      }
      definedPrices.push(n);
    }
    if (definedPrices.length === 0) next.prices = 'Set at least one price.';

    const rawCompare = form.compareAtPrices && typeof form.compareAtPrices === 'object' ? form.compareAtPrices : {};
    for (const code of currencyCodes) {
      const raw = rawCompare[code];
      if (raw === '' || raw === null || raw === undefined) continue;
      const n = Number(raw);
      if (Number.isNaN(n)) {
        next[`compareAtPrices_${code}`] = `${code} MRP must be a number.`;
        continue;
      }
      if (n < 0) {
        next[`compareAtPrices_${code}`] = `${code} MRP must be 0 or greater.`;
        continue;
      }
      const selling = rawPrices[code];
      const sellingNum = selling === '' || selling === null || selling === undefined ? null : Number(selling);
      if (Number.isFinite(sellingNum) && n < sellingNum) {
        next[`compareAtPrices_${code}`] = `${code} MRP should be greater than or equal to selling price.`;
      }
    }

    if (form.stockQuantity === '' || Number.isNaN(Number(form.stockQuantity))) {
      next.stockQuantity = 'Stock quantity is invalid.';
    }
    if (Number(form.stockQuantity) < 0) next.stockQuantity = 'Stock quantity must be 0 or greater.';

    if (images.length === 0) next.images = 'At least one product image is required.';

    if (!Array.isArray(form.sizes) || form.sizes.length === 0) next.sizes = 'Add at least one size.';
    if (!Array.isArray(form.colors) || form.colors.length === 0) next.colors = 'Add at least one color.';

    return next;
  }, [form, images.length]);

  const isValid = Object.keys(errors).length === 0;

  const setField = (key, value) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'sizes' || key === 'colors') {
        const variantStock = ensureVariantStock(next);
        const stockQuantity = getTotalVariantStock(variantStock);
        return { ...next, variantStock, stockQuantity };
      }
      if (key === 'variantStock') {
        const stockQuantity = getTotalVariantStock(value);
        return { ...next, stockQuantity };
      }
      return next;
    });

    if (key === 'colors') {
      const colors = Array.isArray(value) ? value.map((c) => String(c).toUpperCase()) : [];
      setColorImages((prev) => {
        const out = { ...(prev && typeof prev === 'object' ? prev : {}) };
        colors.forEach((c) => {
          if (!out[c]) out[c] = { images: [], primaryImageIndex: 0 };
        });
        Object.keys(out).forEach((c) => {
          if (!colors.includes(c)) delete out[c];
        });
        return out;
      });
    }
  };

  const onPickImages = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const onPickColorImages = (color) => {
    const c = String(color || '').toUpperCase();
    if (!c) return;
    setActiveColorUpload(c);
    if (colorFileInputRef.current) colorFileInputRef.current.click();
  };

  const onFilesSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const readAsDataUrl = (file) =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
      });

    try {
      const dataUrls = await Promise.all(files.map((f) => readAsDataUrl(f)));
      const mapped = files.map((file, idx) => ({
        id: `${file.name}-${file.lastModified}-${Math.random().toString(16).slice(2)}`,
        file,
        previewUrl: dataUrls[idx],
      }));

      setImages((prev) => [...prev, ...mapped]);
      setTouched((prev) => ({ ...prev, images: true }));
      if (images.length === 0) setPrimaryImageIndex(0);
    } catch (err) {
      toast({
        title: 'Image upload failed',
        description: 'Please try again.',
      });
    } finally {
      e.target.value = '';
    }
  };

  const onColorFilesSelected = async (e) => {
    const color = String(activeColorUpload || '').toUpperCase();
    const files = Array.from(e.target.files || []);
    if (!color || files.length === 0) {
      e.target.value = '';
      return;
    }

    const readAsDataUrl = (file) =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
      });

    try {
      const dataUrls = await Promise.all(files.map((f) => readAsDataUrl(f)));
      const mapped = files.map((file, idx) => ({
        id: `${color}-${file.name}-${file.lastModified}-${Math.random().toString(16).slice(2)}`,
        file,
        previewUrl: dataUrls[idx],
      }));

      setColorImages((prev) => {
        const current = prev && typeof prev === 'object' ? prev : {};
        const entry = current[color] && typeof current[color] === 'object' ? current[color] : { images: [], primaryImageIndex: 0 };
        const nextImages = [...(Array.isArray(entry.images) ? entry.images : []), ...mapped];
        const nextPrimary = nextImages.length === mapped.length ? 0 : Number(entry.primaryImageIndex || 0);
        return {
          ...current,
          [color]: {
            ...entry,
            images: nextImages,
            primaryImageIndex: nextPrimary,
          },
        };
      });
      setTouched((prev) => ({ ...prev, colorImages: true }));
    } catch {
      toast({ title: 'Image upload failed', description: 'Please try again.' });
    } finally {
      e.target.value = '';
    }
  };

  const removeImage = (idx) => {
    setImages((prev) => {
      const next = prev.slice();
      next.splice(idx, 1);
      return next;
    });
    setPrimaryImageIndex((prev) => {
      if (idx === prev) return 0;
      if (idx < prev) return Math.max(0, prev - 1);
      return prev;
    });
  };

  const moveColorImage = (color, from, to) => {
    const c = String(color || '').toUpperCase();
    if (!c) return;
    setColorImages((prev) => {
      const current = prev && typeof prev === 'object' ? prev : {};
      const entry = current[c] && typeof current[c] === 'object' ? current[c] : { images: [], primaryImageIndex: 0 };
      const next = Array.isArray(entry.images) ? entry.images.slice() : [];
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      const prevPrimary = Number(entry.primaryImageIndex || 0);
      let nextPrimary = prevPrimary;
      if (prevPrimary === from) nextPrimary = to;
      else if (from < prevPrimary && to >= prevPrimary) nextPrimary = prevPrimary - 1;
      else if (from > prevPrimary && to <= prevPrimary) nextPrimary = prevPrimary + 1;
      return {
        ...current,
        [c]: {
          ...entry,
          images: next,
          primaryImageIndex: nextPrimary,
        },
      };
    });
  };

  const removeColorImage = (color, idx) => {
    const c = String(color || '').toUpperCase();
    if (!c) return;
    setColorImages((prev) => {
      const current = prev && typeof prev === 'object' ? prev : {};
      const entry = current[c] && typeof current[c] === 'object' ? current[c] : { images: [], primaryImageIndex: 0 };
      const next = Array.isArray(entry.images) ? entry.images.slice() : [];
      next.splice(idx, 1);
      const prevPrimary = Number(entry.primaryImageIndex || 0);
      const nextPrimary = idx === prevPrimary ? 0 : idx < prevPrimary ? Math.max(0, prevPrimary - 1) : prevPrimary;
      return {
        ...current,
        [c]: {
          ...entry,
          images: next,
          primaryImageIndex: nextPrimary,
        },
      };
    });
  };

  const moveImage = (from, to) => {
    setImages((prev) => {
      const next = prev.slice();
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });
    setPrimaryImageIndex((prev) => {
      if (prev === from) return to;
      if (from < prev && to >= prev) return prev - 1;
      if (from > prev && to <= prev) return prev + 1;
      return prev;
    });
  };

  const startNewCategory = () => {
    setShowNewCategory(true);
    setNewCategoryName('');
    setNewCategorySlug('');
  };

  const createCategory = async () => {
    const name = newCategoryName.trim();
    const rawSlug = (newCategorySlug || name).trim();

    if (!name) {
      toast({
        title: 'Missing category details',
        description: 'Please provide a category name.',
      });
      return;
    }

    try {
      const created = await upsertCategory({ name, slug: rawSlug, visible: true });
      setField('categories', Array.from(new Set([...(form.categories || []), created.slug])));
      setShowNewCategory(false);
      toast({ title: 'Category created', description: `${created.name} added.` });
    } catch (e) {
      toast({ title: 'Category create failed', description: String(e?.message || 'Please try again.') });
    }
  };

  const submit = async (mode) => {
    setTouched({
      name: true,
      description: true,
      categories: true,
      prices: true,
      compareAtPrices: true,
      stockQuantity: true,
      sizes: true,
      colors: true,
      images: true,
    });

    if (!isValid) {
      toast({
        title: 'Fix validation errors',
        description: 'Please review the highlighted fields.',
      });
      return;
    }

    if (!id) {
      toast({ title: 'Save failed', description: 'Missing product id.' });
      return;
    }

    setSaving(true);
    try {
      const finalStatus = mode === 'draft' ? 'draft' : form.status;

      const prices = {};
      const rawPrices = form.prices && typeof form.prices === 'object' ? form.prices : {};
      for (const code of currencyCodes) {
        const raw = rawPrices[code];
        if (raw === '' || raw === null || raw === undefined) continue;
        const n = Number(raw);
        if (!Number.isFinite(n)) continue;
        prices[code] = n;
      }
      const legacyPrice = Number.isFinite(Number(prices.USD)) ? Number(prices.USD) : Number(Object.values(prices)[0]);

      const compareAtPrices = {};
      const rawCompare = form.compareAtPrices && typeof form.compareAtPrices === 'object' ? form.compareAtPrices : {};
      for (const code of currencyCodes) {
        const raw = rawCompare[code];
        if (raw === '' || raw === null || raw === undefined) continue;
        const n = Number(raw);
        if (!Number.isFinite(n)) continue;
        compareAtPrices[code] = n;
      }
      const legacyCompareAtPrice = Object.prototype.hasOwnProperty.call(compareAtPrices, 'USD')
        ? Number(compareAtPrices.USD)
        : null;

      const payload = {
        name: form.name.trim(),
        description: form.description.trim(),
        categories: form.categories,
        category: form.categories?.[0] || 'new-arrivals',
        tags: Object.entries(form.tags)
          .filter(([, v]) => Boolean(v))
          .map(([k]) => k),
        prices,
        price: legacyPrice,
        compareAtPrices,
        compareAtPrice: Number.isFinite(Number(legacyCompareAtPrice)) ? Number(legacyCompareAtPrice) : null,
        variantStock: ensureVariantStock(form),
        stockQuantity: Number(getTotalVariantStock(ensureVariantStock(form))),
        inStock: Number(getTotalVariantStock(ensureVariantStock(form))) > 0 && finalStatus !== 'out_of_stock',
        status: finalStatus,
        visible: !!form.visible,
        featured: !!form.featured,
        sizes: Array.isArray(form.sizes) ? form.sizes : ['S', 'M', 'L'],
        colors: Array.isArray(form.colors) ? form.colors : ['BLACK'],
        primaryImageIndex,
        colorImages: {},
      };

      const urls = [];
      for (const img of images) {
        if (!img) continue;
        if (!img.file) {
          if (img.previewUrl) urls.push(img.previewUrl);
          continue;
        }
        // eslint-disable-next-line no-await-in-loop
        const url = await uploadProductImage({
          productId: id,
          file: img.file,
          dataUrl: img.previewUrl,
          filename: img.file.name,
        });
        urls.push(url);
      }

      const colorImagesPayload = {};
      const colorEntries = colorImages && typeof colorImages === 'object' ? colorImages : {};
      for (const [colorKey, entry] of Object.entries(colorEntries)) {
        const imgs = entry && typeof entry === 'object' && Array.isArray(entry.images) ? entry.images : [];
        const primaryIdx = Number(entry?.primaryImageIndex ?? 0);
        const uploaded = [];
        for (const img of imgs) {
          if (!img) continue;
          if (!img.file) {
            if (img.previewUrl) uploaded.push(img.previewUrl);
            continue;
          }
          // eslint-disable-next-line no-await-in-loop
          const url = await uploadProductImage({
            productId: id,
            file: img.file,
            dataUrl: img.previewUrl,
            filename: img?.file?.name,
          });
          uploaded.push(url);
        }
        colorImagesPayload[String(colorKey).toUpperCase()] = {
          images: uploaded,
          primaryImageIndex: Number.isFinite(primaryIdx) && primaryIdx >= 0 ? primaryIdx : 0,
        };
      }

      await upsertProductAdmin(id, { ...payload, images: urls, colorImages: colorImagesPayload });

      toast({
        title: mode === 'draft' ? 'Draft updated' : 'Product updated',
        description: mode === 'draft' ? 'Changes saved as draft (Firebase).' : 'Changes saved (Firebase).',
      });

      navigate('/admin/products');
    } catch (e) {
      toast({
        title: 'Save failed',
        description: String(e?.message || 'Please try again.'),
      });
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="text-sm text-gray-600">Loading product...</div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-[0_10px_30px_rgba(0,0,0,0.06)]">
          <div className="text-sm font-medium tracking-wide">PRODUCT NOT FOUND</div>
          <div className="mt-2 text-sm text-gray-600">This product may have been deleted or is not available in local storage.</div>
          <div className="mt-6">
            <button
              type="button"
              onClick={() => navigate('/admin/products')}
              className="rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-medium hover:bg-gray-50 transition-colors"
            >
              Back to products
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-12">
      <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-medium tracking-wide">EDIT PRODUCT</h1>
          <p className="mt-2 text-sm text-gray-600">Update product details with a clean, admin-only workflow.</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-medium hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => submit('draft')}
            disabled={saving}
            className="rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-medium hover:bg-gray-50 transition-colors disabled:opacity-60"
          >
            Save as Draft
          </button>
          <button
            type="button"
            onClick={() => submit('save')}
            disabled={saving}
            className="rounded-lg bg-black text-white px-5 py-3 text-sm font-medium hover:bg-gray-900 transition-colors disabled:opacity-60"
          >
            Save Changes
          </button>
        </div>
      </div>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-[0_10px_30px_rgba(0,0,0,0.06)]">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium tracking-wide">BASIC PRODUCT DETAILS</div>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-5">
              <div>
                <label className="block text-sm font-medium text-gray-800">Product Name</label>
                <input
                  value={form.name}
                  onChange={(e) => setField('name', e.target.value)}
                  onBlur={() => setTouched((p) => ({ ...p, name: true }))}
                  className={`mt-2 w-full rounded-lg border px-4 py-3 text-sm outline-none transition-colors ${
                    touched.name && errors.name ? 'border-red-500 focus:border-red-600' : 'border-gray-300 focus:border-black'
                  }`}
                  placeholder="e.g. Premium Linen Overshirt"
                />
                {touched.name && errors.name ? <p className="mt-2 text-xs text-red-600">{errors.name}</p> : null}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-800">Product Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setField('description', e.target.value)}
                  onBlur={() => setTouched((p) => ({ ...p, description: true }))}
                  className={`mt-2 w-full rounded-lg border px-4 py-3 text-sm outline-none transition-colors min-h-[140px] ${
                    touched.description && errors.description
                      ? 'border-red-500 focus:border-red-600'
                      : 'border-gray-300 focus:border-black'
                  }`}
                  placeholder="Write a clear, premium product description."
                />
                {touched.description && errors.description ? (
                  <p className="mt-2 text-xs text-red-600">{errors.description}</p>
                ) : null}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-medium text-gray-800">Categories</label>
                    <button
                      type="button"
                      onClick={startNewCategory}
                      className="text-sm text-gray-600 hover:text-black transition-colors"
                    >
                      + New category
                    </button>
                  </div>

                  <div
                    onBlur={() => setTouched((p) => ({ ...p, categories: true }))}
                    className={`mt-2 rounded-lg border p-3 bg-white ${
                      touched.categories && errors.categories ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {categories.map((c) => (
                        <label key={c.id} className="flex items-center gap-2 text-sm text-gray-700">
                          <input
                            type="checkbox"
                            checked={(form.categories || []).includes(c.slug)}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setField(
                                'categories',
                                checked
                                  ? Array.from(new Set([...(form.categories || []), c.slug]))
                                  : (form.categories || []).filter((s) => s !== c.slug)
                              );
                            }}
                          />
                          {c.name}
                        </label>
                      ))}
                    </div>
                  </div>
                  {touched.categories && errors.categories ? (
                    <p className="mt-2 text-xs text-red-600">{errors.categories}</p>
                  ) : null}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-800">Tags / Labels</label>
                  <div className="mt-3 flex flex-wrap gap-3">
                    <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={!!form.tags.newArrival}
                        onChange={(e) => setField('tags', { ...form.tags, newArrival: e.target.checked })}
                      />
                      New Arrival
                    </label>
                    <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={!!form.tags.featured}
                        onChange={(e) => setField('tags', { ...form.tags, featured: e.target.checked })}
                      />
                      Featured
                    </label>
                  </div>
                </div>
              </div>

              {showNewCategory && (
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-5">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="text-sm font-medium text-gray-900">Create new category</div>
                    <button
                      type="button"
                      onClick={() => setShowNewCategory(false)}
                      className="text-sm text-gray-600 hover:text-black transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-800">Category name</label>
                      <input
                        value={newCategoryName}
                        onChange={(e) => setNewCategoryName(e.target.value)}
                        className="mt-2 w-full rounded-lg border border-gray-300 px-4 py-3 text-sm outline-none focus:border-black transition-colors"
                        placeholder="e.g. BLAZERS"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-800">Slug (optional)</label>
                      <input
                        value={newCategorySlug}
                        onChange={(e) => setNewCategorySlug(e.target.value)}
                        className="mt-2 w-full rounded-lg border border-gray-300 px-4 py-3 text-sm outline-none focus:border-black transition-colors"
                        placeholder="e.g. blazers"
                      />
                    </div>
                  </div>
                  <div className="mt-4">
                    <button
                      type="button"
                      onClick={createCategory}
                      className="rounded-lg bg-black text-white px-5 py-3 text-sm font-medium hover:bg-gray-900 transition-colors"
                    >
                      Create Category
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-[0_10px_30px_rgba(0,0,0,0.06)]">
            <div className="text-sm font-medium tracking-wide">PRICING</div>
            <div className="mt-6">
              <div className="text-sm font-medium text-gray-800">Selling Price</div>
              <div className="mt-2 text-xs text-gray-500">Set prices for the currencies you want to support. Unset currencies will display as Not Available.</div>
              {touched.prices && errors.prices ? <p className="mt-2 text-xs text-red-600">{errors.prices}</p> : null}

              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-5">
                {currencyCodes.map((code) => (
                  <div key={code}>
                    <label className="block text-sm font-medium text-gray-800">{code} Price</label>
                    <input
                      type="number"
                      value={form.prices?.[code] ?? ''}
                      onChange={(e) =>
                        setField('prices', {
                          ...(form.prices || {}),
                          [code]: e.target.value,
                        })
                      }
                      onBlur={() => setTouched((p) => ({ ...p, prices: true }))}
                      className={`mt-2 w-full rounded-lg border px-4 py-3 text-sm outline-none transition-colors ${
                        touched.prices && errors[`prices_${code}`]
                          ? 'border-red-500 focus:border-red-600'
                          : 'border-gray-300 focus:border-black'
                      }`}
                      placeholder=""
                      min="0"
                      step={code === 'JPY' ? '1' : '0.01'}
                    />
                    {touched.prices && errors[`prices_${code}`] ? (
                      <p className="mt-2 text-xs text-red-600">{errors[`prices_${code}`]}</p>
                    ) : null}
                  </div>
                ))}
              </div>

              <div>
                <div className="text-sm font-medium text-gray-800">Compare-at / MRP (optional)</div>
                <div className="mt-2 text-xs text-gray-500">Set MRP per currency. Leave blank to hide compare-at price.</div>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-5">
                  {currencyCodes.map((code) => (
                    <div key={code}>
                      <label className="block text-sm font-medium text-gray-800">{code} MRP</label>
                      <input
                        type="number"
                        value={form.compareAtPrices?.[code] ?? ''}
                        onChange={(e) =>
                          setField('compareAtPrices', {
                            ...(form.compareAtPrices || {}),
                            [code]: e.target.value,
                          })
                        }
                        onBlur={() => setTouched((p) => ({ ...p, compareAtPrices: true }))}
                        className={`mt-2 w-full rounded-lg border px-4 py-3 text-sm outline-none transition-colors ${
                          touched.compareAtPrices && errors[`compareAtPrices_${code}`]
                            ? 'border-red-500 focus:border-red-600'
                            : 'border-gray-300 focus:border-black'
                        }`}
                        placeholder=""
                        min="0"
                        step={code === 'JPY' ? '1' : '0.01'}
                      />
                      {touched.compareAtPrices && errors[`compareAtPrices_${code}`] ? (
                        <p className="mt-2 text-xs text-red-600">{errors[`compareAtPrices_${code}`]}</p>
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-800">Product Status</label>
                <select
                  value={form.status}
                  onChange={(e) => setField('status', e.target.value)}
                  className="mt-2 w-full rounded-lg border border-gray-300 px-4 py-3 text-sm outline-none focus:border-black transition-colors bg-white"
                >
                  <option value="draft">Draft</option>
                  <option value="active">Active</option>
                  <option value="out_of_stock">Out of Stock</option>
                </select>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-[0_10px_30px_rgba(0,0,0,0.06)]">
            <div className="text-sm font-medium tracking-wide">SIZES & COLORS</div>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium text-gray-800">Sizes</label>
                <div className="mt-3 flex flex-wrap gap-2">
                  {(form.sizes || []).map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => removeToken('sizes', s)}
                      className="inline-flex items-center gap-2 rounded-full border border-gray-300 bg-white px-3 py-1 text-xs font-medium text-gray-800 hover:bg-gray-50 transition-colors"
                    >
                      {s}
                      <span className="text-gray-500">×</span>
                    </button>
                  ))}
                </div>
                <div className="mt-3 flex gap-2">
                  <input
                    value={sizeInput}
                    onChange={(e) => setSizeInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addToken('sizes', sizeInput);
                        setSizeInput('');
                        setTouched((p) => ({ ...p, sizes: true }));
                      }
                    }}
                    placeholder="e.g. XS"
                    className="flex-1 rounded-lg border border-gray-300 px-4 py-3 text-sm outline-none focus:border-black transition-colors"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      addToken('sizes', sizeInput);
                      setSizeInput('');
                      setTouched((p) => ({ ...p, sizes: true }));
                    }}
                    className="rounded-lg border border-black px-4 py-3 text-sm font-medium hover:bg-black hover:text-white transition-colors"
                  >
                    Add
                  </button>
                </div>
                {touched.sizes && errors.sizes ? <p className="mt-2 text-xs text-red-600">{errors.sizes}</p> : null}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-800">Colors</label>
                <div className="mt-3 flex flex-wrap gap-2">
                  {(form.colors || []).map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => removeToken('colors', c)}
                      className="inline-flex items-center gap-2 rounded-full border border-gray-300 bg-white px-3 py-1 text-xs font-medium text-gray-800 hover:bg-gray-50 transition-colors"
                    >
                      {c}
                      <span className="text-gray-500">×</span>
                    </button>
                  ))}
                </div>
                <div className="mt-3 flex gap-2">
                  <input
                    value={colorInput}
                    onChange={(e) => setColorInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addToken('colors', colorInput);
                        setColorInput('');
                        setTouched((p) => ({ ...p, colors: true }));
                      }
                    }}
                    placeholder="e.g. BLACK"
                    className="flex-1 rounded-lg border border-gray-300 px-4 py-3 text-sm outline-none focus:border-black transition-colors"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      addToken('colors', colorInput);
                      setColorInput('');
                      setTouched((p) => ({ ...p, colors: true }));
                    }}
                    className="rounded-lg border border-black px-4 py-3 text-sm font-medium hover:bg-black hover:text-white transition-colors"
                  >
                    Add
                  </button>
                </div>
                {touched.colors && errors.colors ? <p className="mt-2 text-xs text-red-600">{errors.colors}</p> : null}
              </div>
            </div>
          </div>

          {(form.sizes || []).length > 0 && (form.colors || []).length > 0 ? (
            <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-[0_10px_30px_rgba(0,0,0,0.06)]">
              <div className="text-sm font-medium tracking-wide">INVENTORY</div>
              <div className="mt-6 space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-800">Stock Quantity</label>
                  <input
                    type="number"
                    value={form.stockQuantity}
                    onBlur={() => setTouched((p) => ({ ...p, stockQuantity: true }))}
                    className={`mt-2 w-full rounded-lg border px-4 py-3 text-sm outline-none transition-colors ${
                      touched.stockQuantity && errors.stockQuantity
                        ? 'border-red-500 focus:border-red-600'
                        : 'border-gray-300 focus:border-black'
                    }`}
                    min="0"
                    step="1"
                    readOnly
                  />
                  {touched.stockQuantity && errors.stockQuantity ? (
                    <p className="mt-2 text-xs text-red-600">{errors.stockQuantity}</p>
                  ) : null}
                </div>

                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                  <div className="text-sm font-medium text-gray-800">Manage variant stock in Stock Manager</div>
                  <div className="mt-2 text-sm text-gray-600">
                    To keep inventory edits consistent, stock can’t be changed from the product editor.
                  </div>
                  <div className="mt-4">
                    <button
                      type="button"
                      onClick={() => navigate(`/admin/products/${id}/stocks`)}
                      className="inline-flex items-center justify-center rounded-lg border border-black px-5 py-3 text-sm font-medium tracking-wide hover:bg-black hover:text-white transition-colors"
                    >
                      Add/Update Stocks
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-[0_10px_30px_rgba(0,0,0,0.06)]">
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="text-sm font-medium tracking-wide">PRODUCT IMAGES</div>
                <div className="mt-2 text-sm text-gray-600">Upload, reorder, choose a primary image.</div>
              </div>
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={onFilesSelected}
                />
                <input
                  ref={colorFileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={onColorFilesSelected}
                />
                <button
                  type="button"
                  onClick={onPickImages}
                  className="inline-flex items-center gap-2 rounded-lg border border-black px-4 py-3 text-sm font-medium hover:bg-black hover:text-white transition-colors"
                >
                  <ImagePlus size={18} />
                  Upload Images
                </button>
              </div>
            </div>

            {touched.images && errors.images ? <p className="mt-3 text-xs text-red-600">{errors.images}</p> : null}

            <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {images.map((img, idx) => (
                <div key={img.id} className="border border-gray-200 rounded-xl overflow-hidden bg-white">
                  <div className="aspect-square bg-gray-100 overflow-hidden relative">
                    <img src={img.previewUrl} alt="Product" className="w-full h-full object-cover" />
                    {idx === primaryImageIndex && (
                      <div className="absolute top-2 left-2 text-[11px] tracking-widest font-medium bg-black text-white px-2 py-1 rounded-full">
                        PRIMARY
                      </div>
                    )}
                  </div>
                  <div className="p-3 space-y-2">
                    <button
                      type="button"
                      onClick={() => setPrimaryImageIndex(idx)}
                      className="w-full text-xs font-medium border border-gray-300 rounded-lg px-3 py-2 hover:bg-gray-50 transition-colors"
                    >
                      Set as Primary
                    </button>
                    <div className="grid grid-cols-3 gap-2">
                      <button
                        type="button"
                        disabled={idx === 0}
                        onClick={() => moveImage(idx, idx - 1)}
                        className={`inline-flex items-center justify-center border border-gray-300 rounded-lg px-2 py-2 hover:bg-gray-50 transition-colors ${
                          idx === 0 ? 'opacity-40 cursor-not-allowed' : ''
                        }`}
                        aria-label="Move left"
                      >
                        <ArrowLeft size={16} />
                      </button>
                      <button
                        type="button"
                        disabled={idx === images.length - 1}
                        onClick={() => moveImage(idx, idx + 1)}
                        className={`inline-flex items-center justify-center border border-gray-300 rounded-lg px-2 py-2 hover:bg-gray-50 transition-colors ${
                          idx === images.length - 1 ? 'opacity-40 cursor-not-allowed' : ''
                        }`}
                        aria-label="Move right"
                      >
                        <ArrowRight size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeImage(idx)}
                        className="inline-flex items-center justify-center border border-red-200 text-red-700 rounded-lg px-2 py-2 hover:bg-red-50 transition-colors"
                        aria-label="Remove image"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {images.length === 0 && (
              <div className="mt-6 rounded-xl border border-dashed border-gray-300 p-8 text-center text-sm text-gray-600">
                Upload product images to get started.
              </div>
            )}
          </div>

          {Array.isArray(form.colors) && form.colors.length > 0 ? (
            <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-[0_10px_30px_rgba(0,0,0,0.06)]">
              <div className="text-sm font-medium tracking-wide">COLOR IMAGES</div>
              <div className="mt-2 text-sm text-gray-600">Upload images for each color variant.</div>

              <div className="mt-6 space-y-8">
                {form.colors.map((c) => {
                  const colorKey = String(c || '').toUpperCase();
                  const entry = colorImages && typeof colorImages === 'object' ? colorImages[colorKey] : null;
                  const imgs = entry && typeof entry === 'object' && Array.isArray(entry.images) ? entry.images : [];
                  const primaryIdx = Number(entry?.primaryImageIndex ?? 0);

                  return (
                    <div key={colorKey} className="border border-gray-200 rounded-2xl p-5">
                      <div className="flex items-center justify-between gap-4">
                        <div className="text-sm font-medium tracking-wide">{colorKey}</div>
                        <button
                          type="button"
                          onClick={() => onPickColorImages(colorKey)}
                          className="inline-flex items-center gap-2 rounded-lg border border-black px-4 py-2 text-sm font-medium hover:bg-black hover:text-white transition-colors"
                        >
                          <ImagePlus size={18} />
                          Upload
                        </button>
                      </div>

                      <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                        {imgs.map((img, idx) => (
                          <div key={img.id} className="border border-gray-200 rounded-xl overflow-hidden bg-white">
                            <div className="aspect-square bg-gray-100 overflow-hidden relative">
                              <img src={img.previewUrl} alt={colorKey} className="w-full h-full object-cover" />
                              {idx === primaryIdx && (
                                <div className="absolute top-2 left-2 text-[11px] tracking-widest font-medium bg-black text-white px-2 py-1 rounded-full">
                                  PRIMARY
                                </div>
                              )}
                            </div>
                            <div className="p-3 space-y-2">
                              <button
                                type="button"
                                onClick={() =>
                                  setColorImages((prev) => {
                                    const current = prev && typeof prev === 'object' ? prev : {};
                                    const base = current[colorKey] && typeof current[colorKey] === 'object' ? current[colorKey] : { images: [], primaryImageIndex: 0 };
                                    return { ...current, [colorKey]: { ...base, primaryImageIndex: idx } };
                                  })
                                }
                                className="w-full text-xs font-medium border border-gray-300 rounded-lg px-3 py-2 hover:bg-gray-50 transition-colors"
                              >
                                Set as Primary
                              </button>
                              <div className="grid grid-cols-3 gap-2">
                                <button
                                  type="button"
                                  disabled={idx === 0}
                                  onClick={() => moveColorImage(colorKey, idx, idx - 1)}
                                  className={`inline-flex items-center justify-center border border-gray-300 rounded-lg px-2 py-2 hover:bg-gray-50 transition-colors ${
                                    idx === 0 ? 'opacity-40 cursor-not-allowed' : ''
                                  }`}
                                  aria-label="Move left"
                                >
                                  <ArrowLeft size={16} />
                                </button>
                                <button
                                  type="button"
                                  disabled={idx === imgs.length - 1}
                                  onClick={() => moveColorImage(colorKey, idx, idx + 1)}
                                  className={`inline-flex items-center justify-center border border-gray-300 rounded-lg px-2 py-2 hover:bg-gray-50 transition-colors ${
                                    idx === imgs.length - 1 ? 'opacity-40 cursor-not-allowed' : ''
                                  }`}
                                  aria-label="Move right"
                                >
                                  <ArrowRight size={16} />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => removeColorImage(colorKey, idx)}
                                  className="inline-flex items-center justify-center border border-red-200 text-red-700 rounded-lg px-2 py-2 hover:bg-red-50 transition-colors"
                                  aria-label="Remove image"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {imgs.length === 0 ? (
                        <div className="mt-4 rounded-xl border border-dashed border-gray-300 p-6 text-center text-sm text-gray-600">
                          Upload {colorKey} images.
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>

        <div className="space-y-6">
          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-[0_10px_30px_rgba(0,0,0,0.06)]">
            <div className="text-sm font-medium tracking-wide">VISIBILITY & CONTROL</div>
            <div className="mt-6 space-y-4">
              <label className="flex items-center justify-between gap-4">
                <span className="text-sm text-gray-800">Visible on store</span>
                <input
                  type="checkbox"
                  checked={!!form.visible}
                  onChange={(e) => setField('visible', e.target.checked)}
                />
              </label>

              <label className="flex items-center justify-between gap-4">
                <span className="text-sm text-gray-800">Mark as featured</span>
                <input
                  type="checkbox"
                  checked={!!form.featured}
                  onChange={(e) => setField('featured', e.target.checked)}
                />
              </label>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-[0_10px_30px_rgba(0,0,0,0.06)]">
            <div className="text-sm font-medium tracking-wide">SUMMARY</div>
            <div className="mt-4 text-sm text-gray-700 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Status</span>
                <span className="font-medium text-black">{String(form.status).toUpperCase().replace('_', ' ')}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Visibility</span>
                <span className="font-medium text-black">{form.visible ? 'Visible' : 'Hidden'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Images</span>
                <span className="font-medium text-black">{images.length}</span>
              </div>
              {!isValid && (
                <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  Some required fields are missing.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminProductEdit;
